import React, { useState } from 'react';
import { 
  Typography, 
  Button, 
  Table, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Switch,
  message,
  Popconfirm,
  Alert,
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  ApiOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { N8nInstance } from '../../../shared/types';
import { supabase } from '@/shared/lib/supabase';
import { 
  useN8nInstances, 
  useCreateN8nInstance, 
  useUpdateN8nInstance,
  useDeleteN8nInstance,
} from '../hooks/useN8nInstances';

const { Title } = Typography;

export const N8nInstancesPage: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingInstance, setEditingInstance] = useState<N8nInstance | null>(null);
  const [form] = Form.useForm();
  // Estado para modal de erro customizado
  const [errorModal, setErrorModal] = useState<{ visible: boolean; message: string; title: string }>({ visible: false, message: '', title: 'Atenção' });
  
  // Função utilitária para exibir erro em modal
  const showErrorModal = (msg: string, title: string = 'Atenção') => setErrorModal({ visible: true, message: msg, title });

  // TanStack Query hooks
  const { data: instances = [], isLoading, error } = useN8nInstances();
  const createInstance = useCreateN8nInstance();
  const updateInstance = useUpdateN8nInstance();
  const deleteInstance = useDeleteN8nInstance();

  const columns: ColumnsType<N8nInstance> = [
    {
      title: 'Nome',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <ApiOutlined />
          <span>{text}</span>
        </Space>
      ),
    },
    {
      title: 'URL da API',
      dataIndex: 'api_url',
      key: 'api_url',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (active: boolean) => (
        <span style={{ color: active ? '#52c41a' : '#ff4d4f' }}>
          {active ? 'Ativo' : 'Inativo'}
        </span>
      ),
    },
    {
      title: 'Criado em',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => new Date(date).toLocaleDateString('pt-BR'),
    },
    {
      title: 'Ações',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Editar
          </Button>
          <Popconfirm
            title="Tem certeza que deseja deletar esta instância?"
            onConfirm={() => handleDelete(record.id)}
            okText="Sim"
            cancelText="Não"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              Deletar
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleEdit = (instance: N8nInstance) => {
    setEditingInstance(instance);
    form.setFieldsValue(instance);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      // 1. Valida no backend (edge function)
      const validationResponse = await supabase.functions.invoke('validate-n8n-deletion', {
        body: { n8n_instance_id: id },
      });

      const validationResult = validationResponse.data || validationResponse;

      if (!validationResult.canDelete) {
        if (validationResult.reason === 'instance_has_active_workflows') {
          const workflowInfo = validationResult.linkedWorkflows?.map((w: any) => 
            `<strong>${w.productName}</strong> (${w.workflowIds.length} workflow${w.workflowIds.length > 1 ? 's' : ''})`
          ).join('<br />') || 'workflows';
          showErrorModal(
            `Esta instância N8N está conectada a ${validationResult.linkedWorkflows?.length || 1} processo(s) automático(s) ativo(s):<br /><br />${workflowInfo}<br /><br />Por segurança, não pode ser excluída agora.<br /><br />Desative ou desvincule todos os workflows relacionados antes de tentar excluir esta instância.`,
            'Não é possível excluir'
          );
        } else if (validationResult.reason === 'instance_linked_to_products') {
          const productNames = validationResult.linkedProducts?.map((p: any) => `<strong>${p.name}</strong>`).join('<br />') || 'produtos';
          showErrorModal(
            `Esta instância N8N está sendo utilizada em um ou mais produtos:<br /><br />${productNames}<br /><br />Para excluir, remova o vínculo desta instância com todos os produtos listados.`,
            'Não é possível excluir'
          );
        } else {
          showErrorModal(validationResult.reason || 'Não foi possível excluir a instância N8N devido a uma restrição do sistema. Caso persista, entre em contato com o suporte.', 'Erro');
        }
        return;
      }

      // 2. Se passou na validação backend, executa exclusão
      await deleteInstance.mutateAsync(id);
      message.success('Instância deletada com sucesso');
    } catch (error) {
      console.error('Erro ao deletar instância:', error);
      showErrorModal('Erro ao deletar instância. Tente novamente ou contate o suporte.', 'Erro');
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      console.log('=== INÍCIO DO SUBMIT ===');
      console.log('Valores recebidos:', values);
      
      // Validação básica dos dados
      if (!values.name || !values.api_url || !values.api_key) {
        console.error('Validação falhou - campos obrigatórios vazios');
        message.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      // Prepara os dados para envio
      const instanceData = {
        name: values.name.trim(),
        api_url: values.api_url.trim(),
        api_key: values.api_key.trim(),
        is_active: values.is_active ?? true,
        template_folder_id: values.template_folder_id?.trim() || null,
      };
      
      console.log('Dados preparados para envio:', instanceData);
      console.log('Modo de edição:', !!editingInstance);

      if (editingInstance) {
        console.log('Atualizando instância:', editingInstance.id);
        await updateInstance.mutateAsync({
          id: editingInstance.id,
          ...instanceData,
        });
        message.success('Instância atualizada com sucesso');
      } else {
        console.log('Criando nova instância...');
        const result = await createInstance.mutateAsync(instanceData);
        console.log('Instância criada com sucesso:', result);
        message.success('Instância criada com sucesso');
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingInstance(null);
      console.log('=== FIM DO SUBMIT (SUCESSO) ===');
    } catch (error: any) {
      console.error('=== ERRO NO SUBMIT ===');
      console.error('Erro completo:', error);
      console.error('Mensagem:', error?.message);
      console.error('Stack:', error?.stack);
      const errorMessage = error?.message || 'Erro ao salvar instância';
      message.error(errorMessage);
      console.log('=== FIM DO SUBMIT (ERRO) ===');
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setEditingInstance(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={2}>Instâncias N8N</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setEditingInstance(null);
            setIsModalOpen(true);
          }}
        >
          Nova Instância
        </Button>
      </div>

      {error && (
        <Alert
          message="Erro ao carregar instâncias"
          description={error instanceof Error ? error.message : 'Erro desconhecido'}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        columns={columns}
        dataSource={instances}
        rowKey="id"
        loading={isLoading || deleteInstance.isPending}
      />

      <Modal
        title={editingInstance ? 'Editar Instância' : 'Nova Instância'}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        closable={!(createInstance.isPending || updateInstance.isPending)}
        maskClosable={!(createInstance.isPending || updateInstance.isPending)}
        keyboard={!(createInstance.isPending || updateInstance.isPending)}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          disabled={createInstance.isPending || updateInstance.isPending}
        >
          <Form.Item
            label="Nome"
            name="name"
            rules={[{ required: true, message: 'Por favor, insira o nome' }]}
          >
            <Input placeholder="Minha Instância N8N" />
          </Form.Item>

          <Form.Item
            label="URL da API"
            name="api_url"
            rules={[
              { required: true, message: 'Por favor, insira a URL da API' },
              { type: 'url', message: 'Por favor, insira uma URL válida' },
            ]}
          >
            <Input placeholder="https://n8n.example.com" />
          </Form.Item>

          <Form.Item
            label="API Key"
            name="api_key"
            rules={[{ required: true, message: 'Por favor, insira a API Key' }]}
          >
            <Input.Password placeholder="n8n_api_key_xxxxx" />
          </Form.Item>

          <Form.Item
            label="Pasta ID de Templates"
            name="template_folder_id"
            rules={[{ required: false }]}
          >
            <Input 
              placeholder="ID da pasta que contém os templates no n8n"
            />
          </Form.Item>

          <Form.Item
            label="Ativo"
            name="is_active"
            valuePropName="checked"
            initialValue={true}
          >
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={createInstance.isPending || updateInstance.isPending}
              >
                {(createInstance.isPending || updateInstance.isPending) ? 'Salvando...' : (editingInstance ? 'Atualizar' : 'Criar')}
              </Button>
              <Button onClick={handleCancel} disabled={createInstance.isPending || updateInstance.isPending}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de Erro para Validação de Deleção */}
      <Modal
        title={errorModal.title}
        open={errorModal.visible}
        onOk={() => setErrorModal({ visible: false, message: '', title: 'Atenção' })}
        onCancel={() => setErrorModal({ visible: false, message: '', title: 'Atenção' })}
        okText="Entendi"
        cancelButtonProps={{ style: { display: 'none' } }}
        closable={false}
        maskClosable={false}
        keyboard={false}
        centered
        width={520}
      >
        <p dangerouslySetInnerHTML={{ __html: errorModal.message }} />
      </Modal>
    </div>
  );
};
